import { create } from 'zustand';
import { casesApi } from '../api';
import type { Case, CasesState, CaseStatus, SalesforceCase } from '../types';

// Check if cases array has actually changed (by comparing case numbers and statuses)
function casesChanged(oldCases: Case[], newCases: Case[]): boolean {
  if (oldCases.length !== newCases.length) return true;
  for (const oldCase of oldCases) {
    const newCase = newCases.find(c => c.case_number === oldCase.case_number);
    if (!newCase) return true;
    // Check key fields that would require a re-render
    if (oldCase.status !== newCase.status ||
        oldCase.detected_language !== newCase.detected_language ||
        oldCase.error_message !== newCase.error_message) {
      return true;
    }
  }
  return false;
}

// Extended state to include Salesforce cases
interface ExtendedCasesState extends CasesState {
  salesforceCases: SalesforceCase[];
  isFetchingSalesforce: boolean;
  fetchSalesforceCases: () => Promise<void>;
}

export const useCasesStore = create<ExtendedCasesState>((set, get) => ({
  cases: [],
  salesforceCases: [],
  selectedCase: null,
  isLoading: false,
  isFetchingSalesforce: false,
  error: null,
  filters: {},

  // Fetch new cases from Salesforce queue
  fetchSalesforceCases: async () => {
    set({ isFetchingSalesforce: true, error: null });
    try {
      const cases = await casesApi.fetchFromSalesforce();
      set({ salesforceCases: cases, isFetchingSalesforce: false });
    } catch (error) {
      set({ error: (error as Error).message, isFetchingSalesforce: false });
    }
  },

  // Fetch cases from API
  fetchCases: async () => {
    const currentCases = get().cases;
    // Only set loading on initial fetch, not during polling
    if (currentCases.length === 0) {
      set({ isLoading: true, error: null });
    }
    try {
      const response = await casesApi.list(get().filters);
      // Only update if data actually changed to prevent unnecessary re-renders
      if (casesChanged(currentCases, response.cases)) {
        set({ cases: response.cases, isLoading: false });
      } else if (currentCases.length === 0) {
        set({ isLoading: false });
      }
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false });
    }
  },

  // Refresh cases from Salesforce
  refreshCases: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await casesApi.refresh();
      set({ cases: response.cases, isLoading: false });
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false });
    }
  },

  setCases: (cases) => set({ cases }),

  selectCase: (caseItem) => set({ selectedCase: caseItem }),

  setLoading: (isLoading) => set({ isLoading }),

  setError: (error) => set({ error }),

  setFilters: (filters) =>
    set((state) => ({
      filters: { ...state.filters, ...filters },
    })),

  updateCase: (caseNumber, updates) =>
    set((state) => ({
      cases: state.cases.map((c) =>
        c.case_number === caseNumber ? { ...c, ...updates } : c
      ),
    })),

  // Download cases from Salesforce
  // Note: Spinner state is handled by jobs store, refresh is handled by job completion callback
  downloadCases: async (caseNumbers) => {
    try {
      await casesApi.download(caseNumbers);
      // Job completion callback will refresh the data
    } catch (error) {
      set({ error: (error as Error).message });
    }
  },

  // Translate a single case
  // Note: Spinner state is handled by jobs store, refresh is handled by job completion callback
  translateCase: async (caseNumber) => {
    try {
      await casesApi.translate(caseNumber);
      // Job completion callback will refresh the data
    } catch (error) {
      set({ error: (error as Error).message });
    }
  },

  // Translate multiple cases
  // Note: Spinner state is handled by jobs store, refresh is handled by job completion callback
  translateCases: async (caseNumbers) => {
    try {
      await casesApi.translateBatch(caseNumbers);
      // Job completion callback will refresh the data
    } catch (error) {
      set({ error: (error as Error).message });
    }
  },

  // Approve a case
  approveCase: async (caseNumber) => {
    try {
      await casesApi.approve(caseNumber);
      get().updateCase(caseNumber, { status: 'approved' });
    } catch (error) {
      set({ error: (error as Error).message });
    }
  },

  // Unapprove a case
  unapproveCase: async (caseNumber) => {
    try {
      await casesApi.unapprove(caseNumber);
      get().updateCase(caseNumber, { status: 'ready_for_review' });
    } catch (error) {
      set({ error: (error as Error).message });
    }
  },

  // Upload cases to Salesforce (single or batch)
  // Note: Spinner state is handled by jobs store, refresh is handled by job completion callback
  uploadCases: async (caseNumbers) => {
    try {
      for (const cn of caseNumbers) {
        get().updateCase(cn, { status: 'uploading' });
      }
      await casesApi.upload(caseNumbers);
    } catch (error) {
      // Revert status on error
      for (const cn of caseNumbers) {
        get().updateCase(cn, { status: 'approved' });
      }
      set({ error: (error as Error).message });
    }
  },

  // Restart a case - delete files and re-download
  // Note: Spinner state is handled by jobs store, refresh is handled by job completion callback
  restartCase: async (caseNumber) => {
    try {
      await casesApi.restart(caseNumber);
      // Job completion callback will refresh the data
    } catch (error) {
      set({ error: (error as Error).message });
    }
  },
}));

// Selector functions
export const selectCasesByStatus = (status: CaseStatus | CaseStatus[]) => (state: CasesState) => {
  const statuses = Array.isArray(status) ? status : [status];
  return state.cases.filter((c) => statuses.includes(c.status));
};

export const selectInQueueCases = selectCasesByStatus(['downloaded', 'translating', 'translated', 'generating_pdf', 'ready_for_review']);
export const selectApprovedCases = selectCasesByStatus(['approved', 'uploading', 'uploaded']);
export const selectReturnedCases = selectCasesByStatus(['returned']);
